import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FormInput, registerObject } from "go-form";
import { useListenEvent } from "go-core/hooks";
import { isLicenseExpired } from "go-security/services/license";
import { OrganizationState } from "go-security/services/organizations/types";
import { ReactComponent as CloseButtonSVG } from "../../../images/svg/close.svg";
import { ReactComponent as DotSVG } from "../../../images/svg/dots.svg";
import { ProductApi, TaxApi } from "../../../services/Api/Organization/types";
import { api } from "../../../services/Api/api";
import { focusBarcodeScannerInput } from "./utils";

interface Props {
	isShown: boolean;
	onHide: () => void;
	form: UseFormReturn<any>;
	organization: OrganizationState;
	taxes?: TaxApi[];
}

const BarcodeScanner: FC<Props> = ({ isShown, onHide, form, organization, taxes }) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		register,
		watch,
		setValue,
		getValues,
	} = form;
	const items = watch(`items`);
	const [loading, setLoading] = useState<boolean>(false);
	const timeoutRef = useRef<any>(0);
	const ref = useRef<HTMLInputElement>(null);
	const [searchedProduct, setSearchedProduct] = useState<ProductApi | undefined>(undefined);
	const isExpiringLicenseNotificationVisible = isLicenseExpired(
		organization.license_expires_at,
		organization.license_type
	);

	const handleFocusInput = useCallback((e) => {
		if (e.key === "Enter") {
			focusBarcodeScannerInput();
		}
	}, []);

	useEffect(() => {
		setTimeout(() => {
			focusBarcodeScannerInput();
		}, 50);
	}, []);

	useListenEvent({
		eventName: "keypress",
		callback: (e?: KeyboardEvent) => {
			if (!e) {
				return;
			}
			handleFocusInput(e);
		},
		shouldRunCallbackOnAdd: true,
		shouldRunCallbackOnRemove: true,
	});

	useEffect(() => {
		setTimeout(() => {
			setValue("customId", "");
			if (ref.current) ref.current.value = "";
			setSearchedProduct(undefined);
		}, 200);
	}, [searchedProduct]);

	const onSearchProductByBarcode = async () => {
		const customId = watch("customId");
		return api.organization().getProductSearchSelect("", { customId });
	};

	const onPreventEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const onChange = (value: string) => {
		let products: ProductApi[] = [];
		setValue("customId", value);
		setLoading(true);
		if (timeoutRef.current) clearTimeout(timeoutRef.current);

		timeoutRef.current = setTimeout(async () => {
			const res = await onSearchProductByBarcode();
			products = [...res];
			setLoading(false);
			onSetProduct(products);
		}, 450);
	};

	const onSetProduct = (products: ProductApi[]) => {
		const product = products[0];
		if (product) {
			setSearchedProduct(product);
			const existingItem = items.find(
				(item: Record<string, any>) => item?.product_id?.toString() === product?.id?.toString()
			);
			const lastItemIndex = items?.length - 1 || 0;
			if (existingItem) {
				const index = items.indexOf(existingItem);
				setValue(`items.${index}.quantity`, existingItem.quantity + 1);
			} else {
				if (taxes) {
					const productTax = taxes.find((tax) => tax.id === product.tax_id);
					setValue(`items.${lastItemIndex}.tax_id`, product.tax_id);
					setValue(`items.${lastItemIndex}.tax`, productTax);
				}

				registerObject(register, `items.${lastItemIndex}.product`, [
					"measure_type_unit",
					"name",
					"id",
					"last_price_money",
				]);
				setValue(`items.${lastItemIndex}.product`, product);
				setValue(`items.${lastItemIndex}.product_id`, product.id);
				setValue(`items.${lastItemIndex}.quantity`, 1);

				if (product.sale_price_money)
					setValue(`items.${lastItemIndex}.price_net_money.amount`, product.sale_price_money.amount);
				if (product?.default_volume) setValue(`items.${lastItemIndex}.volume`, product.default_volume);
			}
		} else setSearchedProduct(undefined);
	};

	const drawSearchingResult = () => {
		if (loading) {
			return (
				<div className="d-flex flex-column align-items-center">
					<span>{t("modules.product.field.barcode_scanning.title")}</span>
					<DotSVG className="mt-1" />
				</div>
			);
		}

		if (!searchedProduct) return <span>{t("modules.product.field.barcode_not_found.title")}</span>;

		if (!loading && searchedProduct)
			return <Link to={`/${organization.id}/products/${searchedProduct.id}/edit`}>{searchedProduct.name}</Link>;
	};

	const onClose = () => {
		setValue("customId", "");
		onHide();
	};

	return (
		<div
			className={`barcode-scanner-modal ${isShown ? "show" : ""}`}
			style={{ top: isExpiringLicenseNotificationVisible ? "40px" : "10px" }}
		>
			<div className="barcode-scanner-modal-inner">
				<div className="barcode-scanner-modal-inner-header">
					<h4>{t("modules.product.field.barcode_scanner.title")}</h4>
					<CloseButtonSVG className="icon" onClick={onClose} />
				</div>
				<div className="barcode-scanner-modal-inner-body">
					<small className="text-muted mt-3">{t("modules.product.field.barcode_scanner.help_text")}</small>
					<div className="mt-3 d-flex flex-column">
						<FormInput
							name="customId"
							errors={errors}
							register={register}
							onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => onPreventEnter(event)}
							onChange={(e) => onChange(e.target.value)}
							label={t("common.word.barcode")}
						/>
						{getValues("customId") && (
							<div className="d-flex flex-column mt-2">
								<span>{t("common.word.product")}:</span>
								<div className="result-box mt-1">{drawSearchingResult()}</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default BarcodeScanner;
